import { Link } from "react-router-dom";
import { DoorClosed, Video, ListChecks, CreditCard, ShieldAlert, TrendingUp, FileSignature, UserPlus, Eye, AlertTriangle } from "lucide-react";
import { fmtCurrency } from "../../lib/format";

const Card = ({ title, children, testid, to, cta }) => (
  <div className="rounded-xl border border-brand-border bg-brand-surface p-6 flex flex-col" data-testid={testid}>
    <div className="flex items-center justify-between mb-5">
      <h3 className="font-serif text-xl text-brand-ink tracking-tight">{title}</h3>
      {to && <Link to={to} className="text-xs text-brand-muted hover:text-brand-primary transition-colors">{cta} →</Link>}
    </div>
    {children}
  </div>
);

const Stat = ({ icon: Icon, label, value, sub, bad, to, testid }) => {
  const body = (
    <div className={`flex items-center gap-3 rounded-lg px-3 py-2.5 -mx-3 transition-colors ${to ? "hover:bg-brand-hover" : ""}`} data-testid={testid}>
      <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${bad ? "bg-rose-50 text-rose-600" : "bg-brand-ink/5 text-brand-ink"}`}><Icon className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-brand-ink">{label}</p>
        {sub && <p className="text-[11px] text-brand-tertiary truncate">{sub}</p>}
      </div>
      <span className={`font-serif text-2xl tabular-nums ${bad ? "text-rose-600" : "text-brand-ink"}`}>{value}</span>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

export function ExecSales({ s }) {
  return (
    <Card title="Sales this month" testid="exec-sales-block" to="/crm" cta="CRM">
      <Stat icon={TrendingUp} label="Deals closed" value={s.deals_closed ?? 0} sub={s.deals_value ? `${fmtCurrency(s.deals_value)} new MRR` : "From the sales tracker"} to="/sales" testid="exec-sales-closed" />
      <Stat icon={UserPlus} label="New leads" value={s.new_leads ?? 0} to="/crm" testid="exec-sales-leads" />
      <Stat icon={FileSignature} label="Proposals sent" value={s.proposals_sent ?? 0} to="/proposals" testid="exec-sales-proposals" />
      <Stat icon={Eye} label="Proposals opened" value={s.proposals_opened ?? 0} sub="Unique proposals viewed by prospects" testid="exec-sales-opened" />
    </Card>
  );
}

const names = (items) => items.slice(0, 3).map((i) => `${i.name} (${i.location})`).join(", ");

export function ExecOps({ o }) {
  const d = o.doors, c = o.cameras, t = o.tasks, r = o.ramp;
  return (
    <Card title="Operations alerts" testid="exec-ops-alerts-block">
      {d && d.lockdown.length > 0 && <Stat icon={ShieldAlert} bad label="Lockdown active" value={d.lockdown.length} sub={names(d.lockdown)} to="/doors" testid="exec-ops-lockdown" />}
      <Stat icon={DoorClosed} bad={d && d.offline.length > 0} label="Doors offline" value={d ? d.offline.length : "—"} sub={d ? (names(d.offline) || "All doors online") : "Kisi not connected"} to="/doors" testid="exec-ops-doors-offline" />
      <Stat icon={DoorClosed} bad={d && d.unlocked.length > 0} label="Doors left unlocked" value={d ? d.unlocked.length : "—"} sub={d ? (names(d.unlocked) || "Outside of schedules") : ""} to="/doors" testid="exec-ops-doors-unlocked" />
      <Stat icon={Video} bad={c && c.offline.length > 0} label="Cameras offline" value={c ? c.offline.length : "—"} sub={c ? `${c.total} cameras total` : "UniFi not connected"} to="/cameras" testid="exec-ops-cameras" />
      <Stat icon={ListChecks} bad={t && t.count > 0} label="Overdue tasks" value={t ? t.count : "—"} sub={t?.items?.[0] ? `Oldest: ${t.items[0].title}` : "Notion boards"} to="/boards" testid="exec-ops-tasks" />
      <Stat icon={CreditCard} bad={r && r.uncoded > 0} label="Uncoded card charges" value={r ? r.uncoded : "—"} sub={r ? `${fmtCurrency(r.uncoded_amount)} · ${r.missing_receipts} missing receipts (30 days)` : "Ramp not connected"} to="/ramp" testid="exec-ops-ramp" />
    </Card>
  );
}

const TAG = { delinquent: ["Delinquent", "bg-rose-50 text-rose-700 border-rose-200"], notice: ["On notice", "bg-amber-50 text-amber-800 border-amber-200"], ending: ["Ending soon", "bg-sky-50 text-sky-700 border-sky-200"] };

export function ExecAttention({ rows, total, onOpen }) {
  return (
    <Card title="Needs attention" testid="exec-needs-attention-block" to="/members" cta={`All ${total}`}>
      <div className="-mx-3 divide-y divide-brand-border max-h-[340px] overflow-y-auto">
        {rows.map((m) => (
          <button key={m.company_id} onClick={() => onOpen(m.company_id)} data-testid={`exec-attention-${m.company_id}`}
            className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-brand-hover transition-colors">
            {m.reasons.includes("delinquent") ? <AlertTriangle className="h-3.5 w-3.5 text-rose-600 shrink-0" /> : <span className="w-3.5" />}
            <span className="min-w-0 flex-1">
              <span className="block text-sm text-brand-ink truncate">{m.company_name}</span>
              <span className="block text-[11px] text-brand-tertiary">{m.locations.join(", ")} · {fmtCurrency(m.mrr)}/mo</span>
            </span>
            <span className="flex gap-1 shrink-0">{m.reasons.map((r) => <span key={r} className={`text-[10px] rounded-full border px-2 py-0.5 ${TAG[r][1]}`}>{TAG[r][0]}</span>)}</span>
          </button>
        ))}
        {!rows.length && <p className="px-3 py-4 text-sm text-brand-muted">Nothing flagged right now.</p>}
      </div>
    </Card>
  );
}
